import lodash from 'lodash';
import { StrHlp } from './string.helper';

/**
 * Functions for handling search queries
 */
export class SearchHlp {
  /**
   * Trims and lowercases text for search comparisons.
   * @param {string} value text to normalize
   * @return {string}
   */
  public static trimAndToLower(value: string): string {
    return StrHlp.mergeWhitespace(value).trim().toLocaleLowerCase();
  }

  /**
   * Find first element matching query
   * @param {T[]}    data
   * @param {string} path  to data field; null - entire object is key (for generic cases; for specific use string variant)
   * @param {string} query key
   * @return {T | undefined}
   */
  public static findFirst<T>(data: T[], path: string | null, query: string): T | undefined;

  /**
   * Find first element matching query
   * @param {string[]} data
   * @param {string}   query
   * @return {string | undefined}
   */
  public static findFirst(data: string[], query: string): string | undefined;

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static findFirst<T>(data: T[], p2: string | null, p3?: string): T | undefined {
    const path = p3 === undefined ? null : p2;
    const query = p3 ?? p2!;
    return SearchHlp._findBase('find', data, path, query) as T | undefined;
  }

  /**
   * Find all elements matching query
   * @param {T[]}    data
   * @param {string} path  to data field; null - entire object is key (for generic cases; for specific use string variant)
   * @param {string} query key
   * @return {T[]}
   */
  public static findAll<T>(data: T[], path: string | null, query: string): T[];

  /**
   * Find all elements matching query
   * @param {string[]} data
   * @param {string}   query
   * @return {string[]}
   */
  public static findAll(data: string[], query: string): string[];

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static findAll<T>(data: T[], p2: string | null, p3?: string): T[] {
    const path = p3 !== undefined ? p2 : null;
    const query = p3 ?? p2!;
    return SearchHlp._findBase('filter', data, path, query) as T[];
  }

  /**
   * Base of find methods
   * @param {'find' | 'filter'} method of system Array to call on data
   * @param {T[]}               data
   * @param {string | null}     path
   * @param {string}            query
   * @return {T[] | T | undefined} T[] for filter, ?T for find
   */
  private static _findBase<T>(method: 'find' | 'filter', data: T[], path: string | null, query: string): T[] | T | undefined {
    const sQuery = query.split(/\s/g);
    const getKey: (x: T) => string = path ? (x) => lodash.get(x, path) : (x) => x;
    return data[method]((x) => SearchHlp._match(getKey(x), sQuery));
  }

  /**
   * Checks if given key matches query, i.e. contain each whitespaces - separated substring
   * @param {string} key
   * @param {string} query
   * @return {boolean}
   */
  public static match(key: string, query: string): boolean {
    return this._match(key, query.split(/\s/g));
  }

  /**
   * Checks if key includes all of sQueries
   * @param {string}   key
   * @param {string[]} sQuery
   * @return {boolean}
   */
  private static _match(key: string, sQuery: string[]): boolean {
    const keyLower = (key ?? '').toLowerCase();

    for (const queryElem of sQuery) {
      if (!keyLower.includes(queryElem.toLowerCase())) return false;
    }
    return true;
  }

  /**
   * Builds regex for fuzzy search
   * @param {string} query
   * @return {RegExp}
   */
  public static regexForFuzzy(query: string): RegExp {
    const pattern = query.split('').join('.*');
    return new RegExp(pattern, 'i');
  }

  /**
   * Checks if key or any of tags matches regex
   * @param {RegExp}   re
   * @param {string}   key
   * @param {string[]} tags
   * @return {boolean}
   */
  public static fuzzyFind(re: RegExp, key: string, tags?: string[]): boolean {
    const tagsInclude = tags?.some((t) => t.match(re)) ?? false;
    return (!!key.match(re) || tagsInclude) ?? false;
  }
}
